import React, { useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { useFirebase } from "@/context/firebase-context";
import { useAuth } from "@/context/auth-context";
import {
  FeedbackContext,
  type FeedbackContextValue,
} from "@/context/feedback-context";
import { trackFeedbackSubmitted } from "@/lib/analytics";
import type { FeedbackInput } from "@/types/feedback";

// Scopes one report's feedback target (drug_id + version + query) and exposes a
// single `submitFeedback` so any descendant — the report-level bar or a
// finding-level flag — can write a version-tagged `feedback` doc without prop
// drilling the identity through the whole report tree.

interface FeedbackProviderProps {
  drugId?: string;
  reportVersion?: number;
  query?: string;
  children: React.ReactNode;
}

export function FeedbackProvider({
  drugId,
  reportVersion,
  query,
  children,
}: FeedbackProviderProps) {
  const { addDocument } = useFirebase();
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);

  const submitFeedback = async (input: FeedbackInput): Promise<void> => {
    if (!user) return;
    setSubmitting(true);
    try {
      const id = uuidv4();
      // Firestore rejects `undefined` field values, so build the doc with only
      // the fields that actually have a value (null is allowed; undefined is not).
      const payload: Record<string, unknown> = {
        id,
        scope: input.scope,
        sentiment: input.sentiment,
        category: input.category,
        drug_id: drugId ?? null,
        report_version: reportVersion ?? null,
        query: query ?? null,
        user_id: user.uid,
        status: "new",
        created_at: new Date().toISOString(),
      };

      const text = input.text?.trim();
      if (text) payload.text = text;

      if (input.target) {
        const target: Record<string, string> = {};
        if (input.target.section) target.section = input.target.section;
        if (input.target.source_id) target.source_id = input.target.source_id;
        if (input.target.excerpt) target.excerpt = input.target.excerpt;
        if (Object.keys(target).length > 0) payload.target = target;
      }

      await addDocument("feedback", payload, id);
      trackFeedbackSubmitted();
    } catch (error) {
      // Feedback is best-effort — never let a failed write interrupt her reading.
      console.error("Failed to submit feedback:", error);
    } finally {
      setSubmitting(false);
    }
  };

  const value: FeedbackContextValue = {
    drugId,
    reportVersion,
    query,
    submitFeedback,
    submitting,
  };

  return (
    <FeedbackContext.Provider value={value}>
      {children}
    </FeedbackContext.Provider>
  );
}
